export enum PartnerProviderType {
  BOOKING_COM = "BOOKING_COM",
  VIATOR = "VIATOR",
}

/**
 * `provider`/`hotelFeePercent` — tasks 0028/0029, contract 2026-09-28.
 * `provider` is only present for SUPER_ADMIN/GUESTERIA_ADMIN viewers; every
 * other role (TENANT_ADMIN, HOTEL_ADMIN, FRONT_DESK) gets `source:
 * "guesteria_partners"` instead — no "Viator" anywhere in that UI.
 * `hotelFeePercent` is gone entirely: the backend no longer returns it, and
 * `PUT`ing it now answers 403.
 *
 * `autoSyncEnabled`/`lastSyncAt` removed entirely (task 0036, contract
 * 2026-09-29) — sync is Guesteria-admin-only now, managed from the new
 * per-hotel admin sync endpoints (`AdminHotelPartnerSyncStatus` below), not
 * from this hotel-scoped settings object.
 */
export interface PartnerSettings {
  id: string;
  hotelId: string;
  provider?: PartnerProviderType;
  source?: "guesteria_partners";
  enabled: boolean;
  /** The single "Guesteria Partners" toggle shown to HOTEL_ADMIN/TENANT_ADMIN/FRONT_DESK. */
  partnersEnabled: boolean;
  destinationId?: string;
  destinationName?: string;
  destinationType?: string;
  destination?: {
    destinationId: string | null;
    destinationName: string | null;
    destinationType: string | null;
    regionName: string | null;
    countryName: string | null;
    countryCode: string | null;
    flagEmoji: string | null;
  } | null;
  metadata?: Record<string, unknown>;
  lastMonthRevenue?: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface UpdatePartnerSettingsRequest {
  enabled?: boolean;
  partnersEnabled?: boolean;
  destinationId?: string;
  destinationName?: string;
  destinationType?: string;
}

// --- Admin-only hotel sync management (task 0036, contract 2026-09-29).
// Replaces the hotel-scoped sync/logs endpoints, which now 404. Every call
// takes the hotel's own tenantId as a query param; SUPER_ADMIN/GUESTERIA_ADMIN
// only.

/** `GET /admin/partners/hotels/:hotelId/sync/status?tenantId=`. */
export interface AdminHotelPartnerSyncStatus {
  state: PartnerSyncState;
  syncId: string | null;
  lastSyncAt: string | null;
  lastResult: PartnerSyncResult | null;
  lastError: string | null;
  autoSyncEnabled: boolean;
}

/** `POST /admin/partners/hotels/:hotelId/sync?tenantId=` (202). */
export interface AdminHotelPartnerSyncTriggerResponse {
  status: "queued" | "running";
  syncId: string;
}

export interface UpdateAdminHotelAutoSyncRequest {
  enabled: boolean;
}

/** `PUT /admin/partners/hotels/:hotelId/auto-sync?tenantId=`. */
export interface AdminHotelAutoSyncResponse {
  hotelId: string;
  autoSyncEnabled: boolean;
}

/**
 * `POST /admin/partners/orders/poll-feed` — "Sync bookings now". Contract
 * confirmed 2026-09-29 (task 0033, backend dev@ec22d23c). Synchronous —
 * the response already carries the finished result, nothing to poll.
 */
export type AdminPartnerOrdersPollFeedStatus =
  | "done"
  | "locked"
  | "disabled"
  | "no-credentials";

export interface AdminPartnerOrdersPollFeedResponse {
  status: AdminPartnerOrdersPollFeedStatus;
  bookings: number;
  hotels: number;
  applied: number;
  failed: number;
  unrouted: number;
  /** Shape not specified beyond "errors" — treated as freeform strings until seen live. */
  errors: string[];
}

/**
 * Tenant's Guesteria-Partners commission share — Guesteria staff only
 * (SUPER_ADMIN / GUESTERIA_ADMIN). `GET|PUT /admin/tenants/:tenantId/partner-commission`.
 * Task 0029, contract 2026-09-28.
 */
export interface TenantPartnerCommission {
  tenantId: string;
  /** 0–100 — the tenant's share of the platform commission. */
  sharePercent: number;
  /** The platform's own commission rate — read-only context for the example, not editable here. */
  commissionPercent: number;
  example: {
    booked: number;
    guesteriaCommission: number;
    tenantEarnings: number;
    guesteriaNet: number;
  };
}

export interface UpdateTenantPartnerCommissionRequest {
  sharePercent: number;
}

/**
 * Whether a tenant's Guesteria Partners integration is enabled — Guesteria
 * staff only (SUPER_ADMIN / GUESTERIA_ADMIN). `GET|PUT /admin/tenants/:tenantId/partners`.
 * Distinct from `TenantPartnersSettings` (`types/payments.ts`, field
 * `partnersEnabled`) — the tenant's own read-only self-service view of the
 * same underlying flag, via a different endpoint and field name. Task 0028,
 * contract 2026-09-28.
 */
export interface AdminTenantPartnersSettings {
  enabled: boolean;
}

/**
 * Per-hotel activity-limit override, admin-only (SUPER_ADMIN / Guesteria
 * admin) — hotel managers and tenant admins never see this.
 * `GET/PUT /admin/hotels/:hotelId/partners/limit?tenantId=<uuid>` (contract
 * 2026-09-27, backend dev@52c7061).
 *
 * `maxActivitiesEnabled`/`maxActivities` are this hotel's override; `null`
 * means "follow the provider default" (`defaultMaxActivitiesEnabled` /
 * `defaultMaxActivities`, read-only here — set on the provider). The
 * `effective*` pair is what the sync actually applies.
 */
export interface PartnerHotelActivityLimit {
  hotelId: string;
  provider: string;
  maxActivitiesEnabled: boolean | null;
  maxActivities: number | null;
  defaultMaxActivitiesEnabled: boolean;
  defaultMaxActivities: number;
  effectiveMaxActivitiesEnabled: boolean;
  effectiveMaxActivities: number;
}

/** Omitted field = unchanged; `null` = reset that field to the provider default. */
export interface UpdatePartnerHotelActivityLimitRequest {
  maxActivitiesEnabled?: boolean | null;
  maxActivities?: number | null;
}

export const PartnerSyncState = {
  IDLE: "idle",
  QUEUED: "queued",
  RUNNING: "running",
  SUCCEEDED: "succeeded",
  PARTIAL: "partial",
  FAILED: "failed",
} as const;

export type PartnerSyncState =
  (typeof PartnerSyncState)[keyof typeof PartnerSyncState];

const TERMINAL_PARTNER_SYNC_STATES = new Set<PartnerSyncState>([
  PartnerSyncState.SUCCEEDED,
  PartnerSyncState.PARTIAL,
  PartnerSyncState.FAILED,
]);

export const isTerminalPartnerSyncState = (
  state: PartnerSyncState | null | undefined,
): boolean => (state ? TERMINAL_PARTNER_SYNC_STATES.has(state) : false);

export interface PartnerSyncResult {
  created: number;
  updated: number;
  deactivated: number;
  errors: number;
  skippedPages: number;
  durationMs: number;
}

export interface PartnerSyncStatus {
  state: PartnerSyncState;
  lastSyncAt: string | null;
  lastResult: PartnerSyncResult | null;
  lastError: string | null;
}

/** New async contract: `POST /partners/sync` returns 202 with this body. */
export interface PartnerSyncTriggerResponse {
  status: "queued" | "running";
  syncId: string;
}

export const isPartnerSyncTriggerResponse = (
  value: unknown,
): value is PartnerSyncTriggerResponse =>
  !!value && typeof value === "object" && "syncId" in value;

export interface PartnerDestination {
  destinationId: string;
  name: string;
  type: string;
  parentId?: string;
  latitude?: number;
  longitude?: number;
  countryName?: string;
  countryCode?: string;
  /** Capital or top city of its country. */
  curated?: boolean;
}

export enum ServiceStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}

export enum ServiceAvailability {
  WHOLE_WEEK = "WHOLE_WEEK",
  WEEKDAYS = "WEEKDAYS",
  WEEKENDS = "WEEKENDS",
}

export interface PartnerServiceCategory {
  tagId: number;
  name: string;
  /** Filter options only: every tag of the category group (DV-159). */
  tagIds?: number[];
}

export interface PartnerServiceImage {
  url: string;
  caption?: string;
  isCover?: boolean;
}

export interface PartnerServiceFlags {
  freeCancellation?: boolean;
  likelyToSellOut?: boolean;
  skipTheLine?: boolean;
}

export interface PartnerServiceDestination {
  destinationId: string | null;
  destinationName: string | null;
  destinationType: "COUNTRY" | "REGION" | "CITY" | null;
  regionName: string | null;
  countryName: string | null;
  countryCode: string | null;
  flagEmoji: string | null;
}

export interface PartnerServiceListItem {
  id: string;
  name: string;
  description: string;
  price?: number;
  currency?: string;
  images: PartnerServiceImage[];
  rating?: number;
  reviewCount?: number;
  categories: PartnerServiceCategory[];
  provider: PartnerProviderType;
  partnerProductCode: string;
  partnerServiceUrl?: string;
  destination?: PartnerServiceDestination | null;
  flags: PartnerServiceFlags;
  status: ServiceStatus;
  isRecommended?: boolean;
  availableDays?: Array<ServiceAvailability | string> | null;
  availableFromTime?: string | null;
  availableToTime?: string | null;
}

export interface PriceRange {
  min: number | null;
  max: number | null;
}

export interface PartnerServiceFilterOptions {
  categories: PartnerServiceCategory[];
  priceRange: PriceRange | null;
}

export interface PartnerServicesListResponse {
  data: PartnerServiceListItem[];
  total: number;
  hasMore: boolean;
}

export interface PartnerServicesFilters {
  search?: string;
  tagIds?: number[];
  status?: ServiceStatus[];
  minPrice?: number;
  maxPrice?: number;
  isRecommended?: boolean;
  availability?: ServiceAvailability;
  page?: number;
  limit?: number;
}
